import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  deleteHaDeviceMapping,
  getAllUsers,
  getHaDeviceMappings,
  getHaUnassignedDevices,
  upsertHaDeviceMapping,
} from '@/api/api'
import type { HaDeviceMapping, HaUnassignedDevice, UserListItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useGoBack } from '@/hooks/useGoBack'
import { parseUtcDate } from '@/lib/date-utils'
import { useAppStore } from '@/store/useAppStore'

/** entity_id から device slug プレフィックスを取り出す（バックエンドの extract_entity_prefix と対応） */
const extractEntityPrefix = (entityId: string | null): string | null => {
  if (!entityId || !entityId.includes('.')) return null
  const objectId = entityId.split('.', 2)[1]
  if (!objectId.includes('_')) return null
  return objectId.slice(0, objectId.lastIndexOf('_'))
}

const formatUserLabel = (user: UserListItem): string => user.name || user.email || user.id

export const HaDevices = () => {
  const goBack = useGoBack()
  const [users, setUsers] = useState<UserListItem[]>([])
  const [mappings, setMappings] = useState<HaDeviceMapping[]>([])
  const [unassigned, setUnassigned] = useState<HaUnassignedDevice[]>([])
  const [saving, setSaving] = useState<string | null>(null)
  const loading = useAppStore((state) => state.loading)
  const startLoading = useAppStore((state) => state.startLoading)
  const stopLoading = useAppStore((state) => state.stopLoading)

  const fetchAll = useCallback(async () => {
    startLoading()
    try {
      const [usersRes, mappingsRes, unassignedRes] = await Promise.all([
        getAllUsers(),
        getHaDeviceMappings(),
        getHaUnassignedDevices(),
      ])
      setUsers(usersRes.users)
      setMappings(mappingsRes.mappings)
      setUnassigned(unassignedRes.devices)
    } catch (error) {
      console.error('Failed to fetch HA device data:', error)
      toast.error('データの取得に失敗しました')
    } finally {
      stopLoading()
    }
  }, [startLoading, stopLoading])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const handleAssign = useCallback(
    async (device: HaUnassignedDevice, userId: string) => {
      // device_id があれば device_id マッチ、無ければ entity プレフィックスマッチ
      const useDeviceId = !!device.device_id
      const deviceKey = useDeviceId ? device.device_id : extractEntityPrefix(device.entity_id)
      if (!deviceKey) {
        toast.error('このデバイスはキーを特定できません')
        return
      }
      const key = `${device.device_id ?? ''}|${device.entity_id ?? ''}`
      setSaving(key)
      try {
        const res = await upsertHaDeviceMapping({
          device_key: deviceKey,
          user_id: userId,
          device_name: device.entity_id ?? device.device_id,
          match_type: useDeviceId ? 'device_id' : 'entity_prefix',
        })
        toast.success(`割当しました（過去 ${res.backfilled} 件を更新）`)
        await fetchAll()
      } catch (error) {
        console.error('Failed to assign device:', error)
        toast.error('割当に失敗しました')
      } finally {
        setSaving(null)
      }
    },
    [fetchAll],
  )

  const handleDeleteMapping = useCallback(async (deviceKey: string) => {
    setSaving(deviceKey)
    try {
      await deleteHaDeviceMapping(deviceKey)
      setMappings((prev) => prev.filter((m) => m.device_key !== deviceKey))
      toast.success('マッピングを削除しました')
    } catch (error) {
      console.error('Failed to delete mapping:', error)
      toast.error('削除に失敗しました')
    } finally {
      setSaving(null)
    }
  }, [])

  if (loading > 0) {
    return null
  }

  return (
    <>
      <Header title="HAデバイス管理" onClose={goBack} />
      <div className="p-4 max-w-4xl mx-auto space-y-8">
        {/* 未割当デバイス */}
        <section>
          <h2 className="text-lg font-semibold mb-3">未割当デバイス</h2>
          {unassigned.length === 0 ? (
            <p className="text-sm text-muted-foreground">未割当のデバイスはありません。</p>
          ) : (
            <div className="space-y-3">
              {unassigned.map((device) => {
                const key = `${device.device_id ?? ''}|${device.entity_id ?? ''}`
                return (
                  <div key={key} className="border rounded-lg p-3 bg-card flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{device.entity_id || device.device_id || '不明'}</div>
                      <div className="text-xs text-muted-foreground">
                        {device.event_count} 件
                        {device.last_received_at &&
                          ` ・ 最終受信: ${parseUtcDate(device.last_received_at).toLocaleString('ja-JP')}`}
                      </div>
                    </div>
                    <Select onValueChange={(userId) => handleAssign(device, userId)} disabled={saving === key}>
                      <SelectTrigger className="w-44 shrink-0">
                        <SelectValue placeholder="ユーザーを割当" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {formatUserLabel(user)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* 既存マッピング */}
        <section>
          <h2 className="text-lg font-semibold mb-3">登録済みマッピング</h2>
          {mappings.length === 0 ? (
            <p className="text-sm text-muted-foreground">登録済みのマッピングはありません。</p>
          ) : (
            <div className="space-y-3">
              {mappings.map((mapping) => {
                const user = users.find((u) => u.id === mapping.user_id)
                return (
                  <div
                    key={mapping.device_key}
                    className="border rounded-lg p-3 bg-card flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{mapping.device_name || mapping.device_key}</div>
                      <div className="text-xs text-muted-foreground">
                        {mapping.match_type} ・ {user ? formatUserLabel(user) : mapping.user_id}
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" disabled={saving === mapping.device_key}>
                          削除
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>マッピングを削除しますか？</AlertDialogTitle>
                          <AlertDialogDescription>
                            {mapping.device_name || mapping.device_key} の紐付けを解除します。
                            過去のイベントデータは変更されません。
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>キャンセル</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteMapping(mapping.device_key)}>
                            削除
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </div>
    </>
  )
}
